"use client";

import { useMemo } from "react";
import dynamic from "next/dynamic";
import { Box, Card, CardContent, Divider, Typography } from "@mui/material";
import type { EChartsOption } from "echarts";
import type { YearlyRow } from "../types";
import { formatBillions } from "../utils";

const ReactECharts = dynamic(() => import("echarts-for-react"), { ssr: false });

type StackedYearlyCommitmentChartProps = {
  title: string;
  rows: YearlyRow[];
  years: number[];
  columns: string[];
  palette: string[];
  colorMap?: Record<string, string>;
  labelFormatter?: (label: string) => string;
  showStackTotalInTooltip?: boolean;
  topGrid?: number;
  yAxisTitle?: string;
};

export default function StackedYearlyCommitmentChart({
  title,
  rows,
  years,
  columns,
  palette,
  colorMap,
  labelFormatter,
  showStackTotalInTooltip = false,
  topGrid = 55,
  yAxisTitle = "USD",
}: StackedYearlyCommitmentChartProps) {
  const option = useMemo<EChartsOption>(() => {
    const formatLabel = (label: string) => labelFormatter?.(label) ?? label;

    const formatTooltip = (params: unknown) => {
      const points = Array.isArray(params) ? params : [params];
      const typedPoints = points as Array<{
        axisValueLabel?: string;
        seriesName?: string;
        marker?: string;
        value?: number | string | null;
      }>;

      const nonZeroPoints = typedPoints.filter((point) => {
        const numericValue = Number(point.value ?? 0);
        return Number.isFinite(numericValue) && numericValue > 0;
      });

      const title = typedPoints[0]?.axisValueLabel ?? "";
      if (nonZeroPoints.length === 0) {
        return `${title}<br/>No data`;
      }

      const lines = nonZeroPoints.map((point) => {
        const numericValue = Number(point.value ?? 0);
        const label = formatLabel(String(point.seriesName ?? ""));
        // return `${point.marker ?? ""}<span style="display:inline-block;max-width:260px;white-space:normal;word-break:break-word;vertical-align:top;">${label}</span>: $${formatBillions(numericValue)}`;
        return `<span style="display: flex;"><span>${point.marker ?? ""}</span><span style="display: flex; justify-content: space-between";><span style="display:inline-block;max-width:260px;white-space:normal;word-break:break-word;vertical-align:top;">${label}</span><span style="margin-left: auto">: $${formatBillions(numericValue)}</span></span></span>`;
      });

      const total = nonZeroPoints.reduce((sum, point) => {
        return sum + Number(point.value ?? 0);
      }, 0);

      return showStackTotalInTooltip
        ? `${title}<br/>${lines.join("")}<b>Total: $${formatBillions(total)}</b>`
        : `${title}<br/>${lines.join("")}`;
    };

    const yearRows = rows.filter((row) =>
      years.includes(Math.round(Number(row.DocumentYear))),
    );

    const yearRowByYear = new Map<number, YearlyRow>();
    yearRows.forEach((row) => {
      yearRowByYear.set(Math.round(Number(row.DocumentYear)), row);
    });

    return {
      tooltip: {
        trigger: "axis",
        confine: true,
        extraCssText:
          "max-width: 360px; white-space: normal; word-break: break-word;",
        axisPointer: { type: "shadow" },
        formatter: formatTooltip,
      },
      legend: {
        type: "scroll",
        top: 0,
        itemGap: 14,
      },
      grid: {
        left: 70,
        right: 20,
        top: topGrid,
        bottom: 45,
      },
      xAxis: {
        type: "category",
        data: years,
      },
      yAxis: {
        type: "value",
        name: yAxisTitle,
        nameLocation: "middle",
        nameRotate: 90,
        nameGap: 0,
        axisLabel: {
          formatter: (value: number) => formatBillions(value),
        },
      },
      series: columns.map((column, index) => ({
        name: formatLabel(column),
        type: "bar",
        stack: "total",
        itemStyle: {
          color: colorMap?.[column] ?? palette[index % palette.length],
        },
        emphasis: { disabled: true },
        data: years.map((year) => {
          const row = yearRowByYear.get(year);
          return row ? (row[column] ?? 0) : 0;
        }),
      })),
    };
  }, [
    colorMap,
    columns,
    labelFormatter,
    palette,
    rows,
    showStackTotalInTooltip,
    topGrid,
    yAxisTitle,
    years,
  ]);

  return (
    <Card sx={{ border: "#e6e6e6 solid 1px" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "1rem",
            mb: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#363636" }}>
            {title}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <ReactECharts
          option={option}
          style={{ height: 320, width: "100%" }}
          notMerge
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 1,
          }}
        >
          <Box
            component="img"
            src="/SDF-Logo.png"
            alt="Sovereign Debt Forum logo"
            sx={{
              width: { xs: 92, sm: 110 },
              height: "auto",
              flexShrink: 0,
              opacity: 0.7,
            }}
          />
          <Typography
            sx={{
              mt: 1,
              fontSize: "0.7rem",
              color: "#626367",
              textAlign: "left",
              lineHeight: "1rem",
            }}
          >
            This data reflects the documents available on our platform, not all
            sovereign debt.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
